// src/components/chat/NewChatModal.jsx
// Start-new-chat modal — lists the REAL org directory (any colleague you can
// message), searchable. Replaces the old hardcoded dummyUsers list.
// Reuses GET /api/users/directory (active users in your org, excludes self,
// includes pending / not-yet-logged-in colleagues).
import React, { useState, useEffect } from 'react';

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";

const roleStr = (role) =>
  typeof role === "object" && role !== null ? (role.value || "") : (role || "");

const NewChatModal = ({ onClose, onSelect }) => {
  const [users,   setUsers]   = useState([]);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState("");
  const [search,  setSearch]  = useState("");

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const res = await fetch(`${API_BASE}/api/users/directory`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        });
        if (!res.ok) throw new Error(`directory ${res.status}`);
        const d = await res.json();
        // /api/users/directory → { ok, message, data: [...] }; be tolerant of shapes.
        const list = Array.isArray(d) ? d : (d?.data || d?.users || []);
        // Backend already excludes self, but guard against is_self just in case.
        if (!cancelled) setUsers(list.filter((u) => !u.is_self));
      } catch (e) {
        console.warn("Could not load org directory:", e);
        if (!cancelled) setError("Couldn't load your organisation directory.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  const q = search.trim().toLowerCase();
  const filtered = users.filter((u) => {
    if (!q) return true;
    return (
      (u.full_name || u.name || "").toLowerCase().includes(q) ||
      (u.email || "").toLowerCase().includes(q) ||
      roleStr(u.role).toLowerCase().includes(q)
    );
  });

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center">
      <div className="bg-[#121212] text-white p-6 rounded-xl w-full max-w-md shadow-lg border border-[#00FF99]">
        <h2 className="text-lg font-bold mb-4">Start New Chat</h2>

        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          autoFocus
          placeholder="Search colleagues by name or email…"
          className="w-full mb-4 bg-black/60 border border-[#00FF99]/30 rounded-lg px-3 py-2 text-sm outline-none placeholder-white/30 focus:border-[#00FF99]/60 transition"
        />

        <div className="max-h-72 overflow-y-auto pr-1">
          {loading && (
            <p className="text-sm text-white/40 text-center py-6">Loading colleagues…</p>
          )}

          {!loading && error && (
            <p className="text-sm text-red-400 text-center py-6">{error}</p>
          )}

          {!loading && !error && filtered.length === 0 && (
            <p className="text-sm text-white/40 text-center py-6">
              {users.length === 0
                ? "No colleagues found in your organisation"
                : `No colleagues match "${search.trim()}"`}
            </p>
          )}

          {!loading && !error && filtered.length > 0 && (
            <ul className="space-y-3">
              {filtered.map((u) => {
                const name = u.full_name || u.name || u.email || "Unknown";
                const sub  = roleStr(u.role) || u.email || "";
                return (
                  <li
                    key={u.id}
                    onClick={() => onSelect?.(u)}
                    className="cursor-pointer hover:bg-[#00FF99]/10 border border-[#00FF99] rounded px-4 py-2"
                  >
                    <div className="font-semibold truncate">{name}</div>
                    <div className="text-sm text-gray-400 truncate">{sub}</div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <button onClick={onClose} className="mt-4 text-sm text-[#00FF99] underline">Cancel</button>
      </div>
    </div>
  );
};

export default NewChatModal;
